
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import { useTranslation } from "../hooks/useTranslation";
import { API_BASE } from "../constants/index";
import { getEthereumProvider } from "../contexts/WalletContext";

const REGISTER_ACCOUNT_API = `${API_BASE}/publicApi/registerAccount`;
const GET_QRCODE_API = `${API_BASE}/publicApi/getQrCode`;


export default function Deposit() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Default to USDT since user removed the selection screen
    const tokenCode = searchParams.get("token_code") || "USDT";

    // --- Detail View State ---
    const [isLoading, setIsLoading] = useState(true);
    const [walletAddress, setWalletAddress] = useState("");
    const [qrImage, setQrImage] = useState("");


    // --- Effect for Details View ---
    useEffect(() => {
        const fetchDepositData = async () => {
            const provider = getEthereumProvider();
            if (!provider) {
                toast.error(t("header.installWeb3Wallet"));
                return false;
            }
            setIsLoading(true);
            let accounts = await provider.request({ method: "eth_accounts" });
            if (accounts.length === 0) {
                accounts = await provider.request({ method: "eth_requestAccounts" });
            }
            if (accounts.length > 0) {
                const address = accounts[0];

            try {
                const res = await fetch(REGISTER_ACCOUNT_API, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ wallet_address: address }),
                });
                const result = await res.json().catch(() => ({}));
                const user_id = result.user_id;

                console.log("user_id:", user_id);

                const res2 = await fetch(GET_QRCODE_API, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ user_id: user_id }),
                });
                const result2 = await res2.json().catch(() => ({}));
                console.log("result2:", result2);
                let qrImgSrc = "";
                let depositAddress = "";

                // API returns JSON: { ton: {...}, bnbsmartchain: { qr_code, deposit_address } }
                if (result2.bnbsmartchain) {
                    const bsc = result2.bnbsmartchain;
                    qrImgSrc = bsc.qr_code ?? "";
                    depositAddress = bsc.deposit_address ?? "";
                }

                // Check if deposit address exists, if not show error
                if (!depositAddress && !qrImgSrc) {
                    toast.error(t("deposit.depositAddressDoesNotExist") || "Deposit address does not exist", {
                        style: {
                            background: '#241630',
                            color: '#fff',
                            border: '1px solid rgba(151,112,221,0.2)'
                        }
                    });
                    setWalletAddress("");
                    setQrImage("");
                } else {
                    setWalletAddress(depositAddress);
                    setQrImage(qrImgSrc);
                }
            } catch (error) {
                console.error("Error fetching deposit info", error);
            } finally {
                setIsLoading(false);
            }
            } else {
                setWalletAddress("");
                setQrImage("");
                setIsLoading(false);
            }
        };

        if (tokenCode) {
            fetchDepositData();
        }
    }, [tokenCode]);

    const copyToClipboard = () => {
        if (walletAddress) {
            navigator.clipboard.writeText(walletAddress);
            toast.success(t("deposit.addressCopied") || "Address copied to clipboard", {
                style: {
                    background: '#241630',
                    color: '#fff',
                    border: '1px solid rgba(151,112,221,0.2)'
                }
            });
        }
    };

    // --- Render: Details View (QR Code) Only ---
    return (
        <div className="w-full max-w-md mx-auto relative">
            <Toaster position="top-center" />

            {/* Content Container - No fixed positioning to prevent layout issues */}
            <div className="bg-[#0a0610]/50 rounded-3xl border border-[#9770DD]/20 overflow-hidden shadow-2xl relative">

                {/* Background Pattern localized to card */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10"></div>
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none -z-10"
                    style={{
                        backgroundImage: `radial-gradient(circle at 2px 2px, #9770DD 1px, transparent 0)`,
                        backgroundSize: '40px 40px'
                    }}
                ></div>

                {/* Inner Header */}
                {/* <div className="flex items-center gap-4 px-6 py-4 border-b border-[#9770DD]/10">
                    <button
                        onClick={() => navigate(-1)}
                        className="size-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                        aria-label="Go back"
                    >
                        <i className='bx bx-chevron-left text-2xl text-white'></i>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-6">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>
                    </button>
                    <div className=''> */}
                {/* Empty title as requested in original, or could be "Deposit" */}
                {/* </div>
                </div> */}

                {/* Main Content */}
                <div className="p-6 pb-24">
                    <div className="flex flex-col items-center">

                        {/* Title + History icon */}
                        <div className="w-full flex items-center justify-center mb-6 relative">
                            <h2 className="text-xl font-semibold text-white/90">
                                {t("deposit.receive") || "Receive"} {tokenCode}
                            </h2>
                            <Link
                                to="/deposit-history"
                                className="absolute right-0 flex items-center justify-center size-10 rounded-full text-[#DFD2FE] hover:bg-[#9770DD]/20 transition-colors"
                                aria-label="Deposit history"
                            >
                                <span className="material-symbols-outlined text-2xl">history</span>
                            </Link>
                        </div>

                        {/* Chain Selector */}
                        <div className="mb-8">
                            <span className="px-6 py-2 rounded-full border border-[#9770DD] bg-[#9770DD]/10 text-[#9770DD] font-medium text-sm inline-block">
                                BEP-20 {tokenCode}
                            </span>
                        </div>

                        {/* QR Code Area */}
                        <div className="mb-8 relative group">
                            {/* Glow effect */}
                            <div className="absolute inset-0 bg-[#9770DD]/20 blur-xl rounded-full opacity-50 group-hover:opacity-75 transition-opacity"></div>

                            <div className="relative bg-white p-3 rounded-xl shadow-lg shadow-white/5">
                                {isLoading ? (
                                    <div className="size-[200px] flex items-center justify-center bg-gray-100 rounded-lg">
                                        <div className="animate-spin rounded-full size-8 border-b-2 border-[#9770DD]"></div>
                                    </div>
                                ) : (
                                    <img
                                        src={qrImage || "/assets/img/receive-qrcode.png"}
                                        alt="QR Code"
                                        className="size-[200px] block mx-auto rounded-lg"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Address & Copy */}
                        <div className="w-full mb-8">
                            <div className="flex flex-col items-center gap-3">
                                <div className="text-sm font-mono text-white/70 break-all text-center px-4 py-2 bg-[#1C1226] rounded-lg border border-[#9770DD]/10 w-full">
                                    {walletAddress || t("deposit.walletAddressNotFound") || "Wallet Address Not Found"}
                                </div>
                                <button
                                    onClick={copyToClipboard}
                                    className="text-[#9770DD] text-sm font-bold hover:text-[#b08df1] transition-colors flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-[#9770DD]/10"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="size-4">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                                    </svg>
                                    {t("deposit.copyAddress") || "Copy Address"}
                                </button>
                            </div>
                        </div>

                        {/* Info Message */}
                        <div className="flex items-start gap-3 bg-[#1C1226] p-4 rounded-xl border border-[#9770DD]/20 text-xs text-white/60 leading-relaxed max-w-sm">
                            <div className="mt-0.5 text-[#9770DD] flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5">
                                    <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <p>
                                {t("deposit.sendOnly") || "Send only"} <b className="text-[#9770DD]">BEP-20 {tokenCode}</b> {t("deposit.toThisAddress") || "to this address. Sending any other coins may result in permanent loss."}
                            </p>
                        </div>

                    </div>
                </div>

                {/* Floating Bottom Button - Positioned absolutely within this container or fixed if preferred, sticking to relative for safety */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#0a0610] to-transparent">
                    <Link
                        to="/dashboard"
                        className="flex items-center justify-center w-full h-12 rounded-xl bg-[#9770DD] hover:bg-[#8659d3] text-white font-bold transition-all shadow-lg shadow-[#9770DD]/20"
                    >
                        {t("deposit.done") || "Done"}
                    </Link>
                </div>
            </div>
        </div>
    );
}
